import moment from 'moment';
import { entityStore } from '~/utility/BinSentry-ui-utility';
import { css, html, LitEntity, renderIf, SirenActionMixin } from '../utility/lit-entity';
import { sharedStyles } from '../styles/shared-styles';
import { bannerStyles } from '../styles/banner-styles';
import '../common/bn-loading';
import './parts/import-mapping';
import './parts/import-csv';

class ImportShow extends SirenActionMixin(LitEntity) {
  static get properties() {
    return {
      hideImportButton: { type: Boolean, attribute: false },
      uploadingCsv: { type: Boolean, attribute: false },
      title: { type: String },
      newItemCountWarningText: { type: String, attribute: 'new-item-count-warning-text' },
      importButtonDescriptionRenderer: { type: Function },
      targetGroupGenerator: { type: Function },
      importPreviewRenderer: { type: Function },
      extraItemRenderer: { type: Function },
    };
  }

  static get styles() {
    return [sharedStyles, bannerStyles, css`
      .title-container h1 {
        margin-bottom: .2em;
      }
      .import-body {
        margin: 0 var(--root-margin);
      }
      .import-action-container {
        display: flex;
        align-items: center;
        margin-bottom: 0.5em;
        padding: 0.8em;
      }
      .import-description {
        font-size: 1em;
        padding-left: 0.5em;
      }
      .item-count {
        font-weight: bold;
        font-size: 1.2em;
      }
      .csv-summary {
        margin-top: 1em;
        margin-bottom: 2em;
      }
      .import-mapping {
        margin-bottom: 2em;
      }
    `];
  }

  render() {
    if (!this.entity) {
      return html`<bn-loading></bn-loading>`;
    }

    const { createdAt, state, headers, recordPreview } = this.entity.properties;

    return html`
      <h1>${this.title}</h1>
      <div class="import-body">
        <label>Created:</label>
        <span>${moment(createdAt).format('MMMM D, YYYY h:mm a')}</span>
        ${renderIf(state === 'completed', () => html`
          <div class="banner banner--info">This CSV has been imported</div>
        `)}
        <div class="csv-summary">
          <bn-flash></bn-flash>
          <import-csv
            .href="${this.href}"
            @upload-error="${this._onCsvUploadError}"
            @upload-start="${this._onCsvUploadStart}"
            @upload-complete="${() => this.uploadingCsv = false}"
          ></import-csv>
        </div>
        ${renderIf(!this.uploadingCsv, () => html`
          <div class="import-mapping">
            <import-mapping
              .href="${this.href}"
              .availableHeaders="${headers}"
              .recordPreview="${recordPreview}"
              .targetGroupGenerator="${this.targetGroupGenerator}"
              .importPreviewRenderer="${this.importPreviewRenderer}"
              @view-change="${this._onImportMappingViewChange}"
            ></import-mapping>
          </div>
          ${this._renderImportButton()}
          ${this._renderImportProgress()}
        `)}
        ${this.extraItemRenderer?.(this.entity) || html``}
      </div>
    `;
  }

  _onCsvUploadStart() {
    this.uploadingCsv = true;
    const flash = this.shadowRoot.querySelector('bn-flash');
    flash.clear();
  }

  _onCsvUploadError(e) {
    this.uploadingCsv = false;
    const flash = this.shadowRoot.querySelector('bn-flash');
    flash.critical(e.error);
  }

  _onImportMappingViewChange(e) {
    const { view } = e.detail;
    this.hideImportButton = view !== 'view';
  }

  _renderImportButton() {
    const { state, newItemCount, recordCount } = this.entity.properties;

    if (!['errored', 'valid'].includes(state) || this.hideImportButton) {
      return html``;
    }

    return html`
      ${renderIf(state === 'valid' && recordCount !== newItemCount, () => html`
        <div class="banner banner--warning">${this.newItemCountWarningText}</div>
      `)}
      <div class="import-action-container well">
        <bn-action
          .href="${this.href}"
          showdisabled
          suppressflash
          suppressheader
          actionname="request-import"
          submittext="Import"
        ></bn-action>
        <span class="import-description">${this.importButtonDescriptionRenderer(this.entity)}</span>
      </div>
    `;
  }

  _renderImportProgress() {
    if (this.entity.properties.state === 'processing') {
      return html`<bn-loading><p>Importing...</p></bn-loading>`;
    }
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    if (!this.entity) {
      return;
    }
    const flash = this.shadowRoot.querySelector('bn-flash');
    flash && flash.clear();

    if (['mapping-assigned', 'validating', 'processing'].includes(this.entity.properties.state)) {
      setTimeout(async () => await entityStore.fetchWait(this.href), 1000);
    }
  }
}

window.customElements.define('import-show', ImportShow);
